import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, SegmentedControl, Tooltip } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { claimSignInRetry, signInAndReturn } from '../auth/signIn';
import { SignalExport, SignalSnapshot } from '../api/cppmodelApi';
import { ParametersCard } from './ParametersCard';
import SimulationGraph, { getLegendItems } from './SimulationGraph';
import { ApiGraphData, normalizeGraphData } from './SimulationResults';
import SimulationTable from './SimulationTable';
import { SignalTransform } from './signalTransform';
import { useResizablePane } from './useResizablePane';
import { msToSeconds } from '../utils/time';

type SnapshotMode = '2d' | 'table';

// The snapshot's signals as a run: transforms are already applied, so each
// signal is drawn as is, with its transform kept only to describe it in the
// legend. A label taken more than once gets " (2)", " (3)", ...
const toGraphData = (snapshot: SignalExport): { data: ApiGraphData; transforms: Record<string, SignalTransform> } => {
    const counts = new Map<string, number>();
    const transforms: Record<string, SignalTransform> = {};
    const inputs: ApiGraphData['inputs'] = [];
    const results: ApiGraphData['results'] = [];

    snapshot.signals.forEach((signal) => {
        const key = `${signal.source}:${signal.label}`;
        const count = (counts.get(key) ?? 0) + 1;
        const label = count > 1 ? `${signal.label} (${count})` : signal.label;
        // Values that were not numbers are dropped, holding the value before them
        const points = signal.x
            .map((x, index) => [x, signal.y[index]] as const)
            .filter((point): point is readonly [number, number] => typeof point[1] === 'number');

        counts.set(key, count);
        (signal.source === 'inputs' ? inputs : results).push({
            label,
            x: points.map(([x]) => x),
            y: points.map(([, y]) => y),
        });
        transforms[`${signal.source === 'inputs' ? 'In' : 'Out'}: ${label}`] = {
            scale: signal.scale ?? 1,
            offset: signal.yOffset ?? 0,
            timeShift: (signal.xOffset ?? 0) / 1000,
        };
    });

    return {
        data: normalizeGraphData({ inputs, results, executionTime: snapshot.createdAt }),
        transforms,
    };
};

const formatParameter = (value: unknown) => typeof value === 'string' ? value : JSON.stringify(value);

const saveJson = (snapshot: SignalExport, fileName: string) => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

// A read-only simulation view of a snapshot taken of one execution.
const SnapshotView: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const { snapshot, error, isUnauthorized } = useLoaderData() as SignalSnapshot;
    const { paneWidth, isResizingPane, bodyRef, splitterProps } = useResizablePane();
    const [mode, setMode] = useState<SnapshotMode>('2d');
    const [traceVisibility, setTraceVisibility] = useState<Record<string, boolean>>({});
    const [isLinkCopied, setIsLinkCopied] = useState(false);
    // Set while leaving to sign in again
    const [isSigningIn, setIsSigningIn] = useState(false);

    // An expired sign-in: sign in again and come back to this snapshot, unless
    // that was just tried and the snapshot is still refused.
    useEffect(() => {
        if (isUnauthorized && claimSignInRetry()) {
            setIsSigningIn(true);
            signInAndReturn(auth);
        }
        // Only for the loaded result, not every change of the auth context
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUnauthorized]);

    const graph = useMemo(() => snapshot ? toGraphData(snapshot) : null, [snapshot]);
    const legendItems = useMemo(() => graph ? getLegendItems(graph.data) : [], [graph]);
    const parameters = useMemo(
        () => Object.entries(snapshot?.parameters ?? {}).map(([name, value]) => ({ name, value: formatParameter(value) })),
        [snapshot],
    );

    if (isSigningIn) {
        return <div className="cm-page"><div className="cm-empty">Your sign-in has expired. Signing in again…</div></div>;
    }

    if (!snapshot || !graph) {
        return (
            <div className="cm-page">
                <div className="cm-page-header">
                    <h1 className="cm-page-title">Snapshot</h1>
                </div>
                {isUnauthorized ? (
                    <div className="cm-empty">
                        <p>Your sign-in was not accepted, so this snapshot could not be opened.</p>
                        <Button intent="primary" onClick={() => signInAndReturn(auth)}>Sign in again</Button>
                    </div>
                ) : (
                    <div className="cm-empty">
                        This snapshot could not be opened: {(error ?? 'it was not found').replace(/\.$/, '')}. Snapshots are kept for 7 days and stop working when their run is deleted.
                    </div>
                )}
            </div>
        );
    }

    const simulationName = location.state?.simulationName ?? snapshot.simulationId;
    const runPath = `/results/${encodeURIComponent(snapshot.simulationId)}/executions/${encodeURIComponent(snapshot.executionId)}`;
    const range = snapshot.from !== null || snapshot.to !== null
        ? ` · ${snapshot.from !== null ? msToSeconds(snapshot.from) : 'start'}–${snapshot.to !== null ? msToSeconds(snapshot.to) : 'end'} s`
        : '';

    const toggleTrace = (name: string) => {
        setTraceVisibility((current) => ({ ...current, [name]: current[name] === false }));
    };

    // Show only the given signal, or every signal again when it already is the only one
    const isolateTrace = (name: string) => {
        setTraceVisibility((current) => legendItems.every((item) => (current[item.name] !== false) === (item.name === name))
            ? {}
            : Object.fromEntries(legendItems.map((item) => [item.name, item.name === name])));
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setIsLinkCopied(true);
            window.setTimeout(() => setIsLinkCopied(false), 2000);
        } catch (copyError) {
            console.error(copyError);
        }
    };

    return (
        <div className="cm-results">
            <div className="cm-page-header" style={{ marginBottom: '16px' }}>
                <div>
                    <h1 className="cm-page-title">Snapshot of {simulationName} · run #{snapshot.executionId}</h1>
                    <p className="cm-page-subtitle">
                        Taken {snapshot.createdAt} UTC{range} · kept until {snapshot.expiresAt} UTC
                    </p>
                </div>
                <div className="cm-page-header-actions">
                    <Tooltip content={isLinkCopied ? 'Link copied' : 'Copy a link to this snapshot'}>
                        <Button icon={isLinkCopied ? IconNames.Tick : IconNames.Link} onClick={copyLink}>Copy link</Button>
                    </Tooltip>
                    <Button icon={IconNames.Download} onClick={() => saveJson(snapshot, `${simulationName}-run-${snapshot.executionId}-snapshot.json`)}>
                        JSON
                    </Button>
                    <Tooltip content={`Open the whole of run #${snapshot.executionId}`}>
                        <Button icon={IconNames.TimelineLineChart} onClick={() => navigate(runPath, { state: { simulationName } })}>
                            Open run
                        </Button>
                    </Tooltip>
                </div>
            </div>

            <div className={`cm-results-body${isResizingPane ? ' is-resizing' : ''}`} ref={bodyRef}>
                <Card className="cm-graph-card">
                    <div className="cm-graph-toolbar">
                        <SegmentedControl
                            small
                            options={[{ label: '2D', value: '2d' }, { label: 'Table', value: 'table' }]}
                            value={mode}
                            onValueChange={(value) => setMode(value as SnapshotMode)}
                        />
                        {mode === '2d' && (
                            <span className="cm-graph-hint">Click the graph to place markers A and B, drag to move them · Scroll to zoom</span>
                        )}
                    </div>
                    <div className="cm-graph-area">
                        {mode === '2d' ? (
                            <SimulationGraph data={graph.data} traceVisibility={traceVisibility} />
                        ) : (
                            <SimulationTable
                                data={graph.data}
                                traceVisibility={traceVisibility}
                                fileName={`${simulationName}-run-${snapshot.executionId}-snapshot.csv`}
                            />
                        )}
                    </div>
                </Card>

                <div className="cm-results-splitter" aria-label="Resize signals pane" {...splitterProps} />

                <ParametersCard
                    width={paneWidth}
                    simulationParameters={parameters}
                    legendItems={legendItems}
                    traceVisibility={traceVisibility}
                    onToggleTrace={toggleTrace}
                    onIsolateTrace={isolateTrace}
                    // Only describes how each signal was scaled and shifted: the values already are
                    transforms={graph.transforms}
                    hideEditors={parameters.length === 0}
                />
            </div>
        </div>
    );
};

export default SnapshotView;
